import { useRef, useState, type Ref } from "react";
import { Plus } from "lucide-react";
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList, ComboboxTrigger } from "./motion/combobox";

/** Search selects a catalog value; existing free-entry callers also keep their typed draft. */
export function SmartSelect({
  value, onChange, options, placeholder, allowOther = true, id, maxLength, compact = false,
  invalid, describedBy, disabled = false, inputRef, preserveValueOnFocus = false,
  "data-company-field": companyField,
}: {
  value: string; onChange: (value: string) => void; options: string[]; placeholder?: string;
  allowOther?: boolean; id?: string; maxLength?: number; compact?: boolean;
  invalid?: boolean; describedBy?: string; disabled?: boolean; inputRef?: Ref<HTMLInputElement>;
  preserveValueOnFocus?: boolean; combobox?: boolean; "data-company-field"?: string;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const keyboardStarted = useRef(false);
  const choices = [...new Set(options)];
  const custom = query.trim();
  const canCreate = allowOther && custom && !choices.some(option => option.toLowerCase() === custom.toLowerCase());
  return <Combobox value={value} onValueChange={onChange} query={query} onQueryChange={next => { setQuery(next); keyboardStarted.current = false; }}
    disabled={disabled} open={open} onOpenChange={next => {
      if (next && !open) { setQuery(preserveValueOnFocus ? value : ""); keyboardStarted.current = false; }
      setOpen(next);
    }} filter={(option, search) => option.toLowerCase().includes(search.trim().toLowerCase())}>
    <ComboboxTrigger className={`min-w-0 w-full bg-white border-line ${compact ? "h-9" : "h-11"} ${invalid ? "border-brand-red" : ""}`}>
      <ComboboxInput ref={inputRef} id={id} aria-label={id ? undefined : placeholder || "Choose a value"}
        data-company-field={companyField} maxLength={maxLength} placeholder={placeholder || "Select or type…"}
        aria-invalid={invalid || undefined} aria-describedby={describedBy} className={compact ? "h-9" : "h-11"}
        onKeyDown={event => {
          // The first arrow enters the highlighted result; subsequent arrows move through it.
          if ((event.key === "ArrowDown" || event.key === "ArrowUp") && !keyboardStarted.current) {
            keyboardStarted.current = true; if (open) event.preventDefault();
          }
        }}
        onChange={event => { if (allowOther) onChange(event.target.value); }} />
    </ComboboxTrigger>
    <ComboboxContent className="bg-white border-line shadow-lift">
      <ComboboxList ariaLabel="Suggestions">
        {choices.map(option => <ComboboxItem key={option} value={option} textValue={option}>{option}</ComboboxItem>)}
        {canCreate && <ComboboxItem value={custom} textValue={custom}><span className="flex items-center gap-2"><Plus className="h-4 w-4 text-brand-red" aria-hidden="true" />Use “{custom}”</span></ComboboxItem>}
        <ComboboxEmpty>No matches.</ComboboxEmpty>
      </ComboboxList>
    </ComboboxContent>
  </Combobox>;
}
